import { goto } from '$app/navigation';
import { AnalyticsService } from '$lib/services/analytics';
import Logger from '$lib/services/analytics/events';
import { EventType } from '$lib/services/analytics/events/common';
import LTVService from '$lib/services/analytics/ltv';
import { ApiService } from '$lib/services/api';
import { Sentry } from '$lib/services/sentry';
import { loadingService } from '$lib/services/loading';
import {
	AOFL_2_0_ROLES,
	CAMPAIGN_PIXEL_TYPES,
	COOKIES,
	COOKIES_CONSENT,
	CURRENCY_CODE,
	GTM_EVENT_NAMES,
	LOCAL_STORAGE_KEYS,
	THIRD_PARTY_EVENTS,
} from '$lib/utils/constants';
import { getDeviceId } from '$lib/utils/device-id';
import SubscriptionProcessor, { PAYMENT_TYPE, SUBSCRIPTION_TYPES } from '@mkt-client/subscription';
import Cookies from 'js-cookie';
import { get } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from '$lib/services/app';
import { app } from '$lib/stores/app/store';
import * as UserStore from '$lib/stores/user/store';
import { UnknownError } from '$lib/stores/subscription/modules/errors';
import { saveSubscriptionStateToStore } from '$lib/stores/subscription/store';
import { ProductExperienceService } from '$lib/services/product-experience';
import { postAuthenticationPathService } from '$lib/services/post-authentication-path';
import { getAnalyticsIdentifiers } from '$lib/services/analytics/identifiers';
import APIError from '$lib/services/api/error';

export {
	crossProductSignUp,
	findEligibleProductsToRestore,
	getLatestSubscriptionReceipt,
	subscribe,
	isSubscriptionPauseEligible,
	upgrade,
	pauseSubscription,
	resumeSubscriptionFromPause,
};

/**
 * @typedef {object} MetaInfo
 * @property {string} product
 * @property {string} paymentType
 * @property {string} price
 * @property {string} currency
 * @property {string} [regPathKey]
 * @property {EventType} [logType]
 * @property {string} [specialOfferUrl]
 */

/**
 * @typedef {object}  CredentialsInfo
 * @property {string} username
 * @property {string} [password]
 */

/**
 * @typedef {object} SubsriptionInformation
 * @property {object} paymentInfo - payment information
 * @property {CredentialsInfo} [credentialsInfo] - credentials information
 * @property {MetaInfo} metaInfo - meta information
 */

/**
 * @typedef {object}  GiftCredentialsInfo
 * @property {string} usernameGiftee
 * @property {string} usernameGifter
 */

/**
 * @typedef {object} GiftPurchaseInformation
 * @property {object} paymentInfo - payment information
 * @property {GiftCredentialsInfo} [giftCredentialsInfo] - credentials information
 * @property {MetaInfo} metaInfo - meta information
 */

/**
 * @param {SubsriptionInformation} subscriptionDetails - details of the subscription request
 * @param {{ paymentType: PAYMENT_TYPE;
 * 			 subscriptionType: string;
 * 			 redirect: boolean; }} options - options for type of subscription and payment
 * @returns {Promise<{error: Error| null; data: object | null;}>} newUserState | error
 * @throws {UnknownError | APIError}
 */
async function subscribe(subscriptionDetails, options) {
	const { subscriptionType, paymentType, redirect } = options;
	const {
		metaInfo: { logType, specialOfferUrl, product, price, currency },
	} = subscriptionDetails;
	let ltv = LTVService.DEFAULT_LTV;
	const logPayload = {
		currency,
		ltv,
		price,
		product,
		subscriptionInfo: {},
		subscriptionType,
		conversionEventId: uuidv4(),
	};

	return loadingService.withLoading(async () => {
		try {
			const appService = new AppService();

			const processor = SubscriptionProcessor(ApiService, appService, {
				subscriptionType: options.subscriptionType,
				paymentType: options.paymentType,
			});

			const subResponse = await processor.handle(subscriptionDetails);

			if (specialOfferUrl) await ApiService.setSpecialOffer(specialOfferUrl);

			const newUserState = await ApiService.validateLogin();
			UserStore.publishSuccess({
				...subResponse,
				...newUserState,
				...(subscriptionDetails.credentialsInfo?.password && {
					password: subscriptionDetails.credentialsInfo?.password,
				}),
			});

			// Persist analytics identifiers after successful subscribe/resubscribe
			try {
				const identifiers = getAnalyticsIdentifiers();
				if (subResponse?.user_to_product_uuid) {
					await ApiService.subscriberSetAnalyticsIdentifiers({
						user_to_product_uuid: subResponse.user_to_product_uuid,
						identifiers,
						mode: 'merge',
						precedence: 'new_precedence',
					});
				}
			} catch (err) {
				Sentry.captureException(err, {
					extra: {
						action: 'Error: SetAnalyticsIdentifiers after subscribe',
					},
				});
			}

			try {
				const subscriptionInfo = await ApiService.getSubscriptionInfo({
					subscription_uuid: subResponse?.subscription_uuid ?? null,
				});
				logPayload.subscriptionInfo = subscriptionInfo;
				const renewalInfo = {
					renewalPeriod: subscriptionInfo.renewal_period ?? null,
					renewalTerm: subscriptionInfo.renewal_term ?? null,
				};
				ltv = await LTVService.getLtv(renewalInfo, logPayload);
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: getSubscriptionInfo',
						subscriptionType,
						paymentType,
					},
				});
			}
			logPayload.ltv = ltv;

			if (logType) await logEvent(logType, logPayload, get(UserStore.user));

			if (subscriptionType === SUBSCRIPTION_TYPES.NEW_SUBSCRIPTION) {
				// TODO: move bottom three calls to transports
				const sourceInfoJson = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO) ?? '{}';
				const sourceInfo = JSON.parse(sourceInfoJson) || {};
				const conversionEventId = uuidv4();
				const eventSource = sourceInfo?.id ? [sourceInfo?.id] : [];

				const cardName = subscriptionDetails?.paymentInfo?.cardName;
				const { firstname, lastname } = cardName
					? AnalyticsService.parseFullName(cardName)
					: { firstname: '', lastname: '' };

				const userState = get(UserStore.user);
				const mobile = userState?.tmpPhone
					? AnalyticsService.formatPhoneForGTM(userState.tmpPhone)
					: '';

				await Promise.allSettled([
					AnalyticsService.fireInternalPixels(CAMPAIGN_PIXEL_TYPES.CONVERSION, {
						user_to_product_uuid: subResponse.user_to_product_uuid,
					}),
					AnalyticsService.trackThirdPartyConversion({
						conversion_event_id: conversionEventId,
						country_code: subscriptionDetails?.credentialsInfo?.countryAbbrv,
						email: subscriptionDetails?.credentialsInfo?.username || null,
						event_info: {
							...(sourceInfo?.values ? sourceInfo?.values : {}),
							analytics_cookies_enabled: Cookies.get(COOKIES_CONSENT.ANALYTICS) === 'true',
							fbc: Cookies.get(COOKIES.FBC),
							fbp: Cookies.get(COOKIES.FBP),
							ltv,
							ltv_currency: 'USD', // LTV always in USD
							product_hash: product,
							targeting_cookies_enabled: Cookies.get(COOKIES_CONSENT.TARGETING) === 'true',
						},
						event_source: eventSource,
						event_type: THIRD_PARTY_EVENTS.SUBSCRIBE,
						user_to_product_uuid: subResponse.user_to_product_uuid,
					}),
					AnalyticsService.fireCampaignPixel(CAMPAIGN_PIXEL_TYPES.CONVERSION),
					AnalyticsService.dataLayerPush({
						currency,
						email: subscriptionDetails?.credentialsInfo?.username,
						espRecipientID: subResponse.esp_recipient_id,
						event_id: conversionEventId,
						eventName: GTM_EVENT_NAMES.CONVERSION,
						fbc: Cookies.get(COOKIES.FBC),
						fbp: Cookies.get(COOKIES.FBP),
						firstname,
						language: navigator.language ? navigator.language.split('-')[0] : '',
						lastname,
						ltv,
						mobile,
						product,
						state: get(app)?.meta?.viewerCountryRegion ?? '',
						user_to_product_uuid: subResponse.user_to_product_uuid,
						value: price,
						zip: subscriptionDetails?.paymentInfo?.zipCode ?? '',
					}),
				]);
			}

			if (redirect) return await goToNextRegPathStepHelper();

			return { data: get(UserStore.user), error: null };
		} catch (error) {
			let err = error instanceof Error ? error : new UnknownError(error);
			UserStore.publishFailure(err);
			// Capture error in Sentry unless explicitly marked as non-fatal
			if (error?.metadata?.fatal !== false) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: Subscribe Action',
						paymentType,
						subscriptionType,
					},
				});
			}

			if (err?.cause?.errorCode === 992323) {
				await AnalyticsService.trackClienServerSidetValidation({
					form: {
						prePaidCards: {
							valid: false,
							errors: {
								message: 'Pre-paid cards are not allowed',
							},
						},
					},
					component: 'form-payment',
				});
			}

			return { error: err, data: null };
		}
	});
}

/**
 * @param {{ actionSource: string;
 *			currency?: string;
 *			logType?: EventType | null;
 *			ltv?: string;
 *			paymentInfo?: object
 *			price?: string;
 *			subscriptionHash: string; }} upgradeDetails - upgrade payload
 * @param {object} options - options for handling the request
 * @param {boolean} options.redirect - whether to re-direct after request is complete
 * @param {string} options.paymentType - type of payment type
 * @returns {Promise<{ data: object; error: null | Error }>} - the user state
 */
async function upgrade(
	upgradeDetails,
	options = { redirect: true, paymentType: PAYMENT_TYPE.CREDIT_CARD }
) {
	return loadingService.withLoading(async () => {
		const { subscriptionHash, price, logType } = upgradeDetails;
		try {
			const appService = new AppService();
			const processor = SubscriptionProcessor(ApiService, appService, {
				subscriptionType: SUBSCRIPTION_TYPES.EXISTING_SUBSCRIPTION,
				paymentType: options.paymentType,
			});
			const upgradeState = await processor.handleUpgrade(upgradeDetails);
			const newUserState = await ApiService.validateLogin();
			UserStore.publishSuccess({ ...upgradeState, ...newUserState });
			const newUser = get(UserStore.user);

			// Persist analytics identifiers after successful upgrade
			try {
				const identifiers = getAnalyticsIdentifiers();
				const userToProductUuid = upgradeState?.user_to_product_uuid ?? newUser?.userToProductUuid;
				if (userToProductUuid) {
					await ApiService.subscriberSetAnalyticsIdentifiers({
						user_to_product_uuid: userToProductUuid,
						identifiers,
						mode: 'merge',
						precedence: 'new_precedence',
					});
				}
			} catch (err) {
				Sentry.captureException(err, {
					extra: {
						action: 'Error: SetAnalyticsIdentifiers after upgrade',
					},
				});
			}

			// log with relevant transports
			const logPayload = {
				conversionEventId: '',
				currency: upgradeDetails?.currency ?? 'USD',
				ltv: upgradeDetails?.ltv ?? LTVService.DEFAULT_LTV,
				price,
				product: subscriptionHash,
				subscriptionType: SUBSCRIPTION_TYPES.EXISTING_SUBSCRIPTION,
				subscriptionInfo: {},
			};

			try {
				const subscriptionInfo = await ApiService.getSubscriptionInfo({
					subscription_uuid: upgradeState?.subscription_uuid ?? null,
				});
				logPayload.subscriptionInfo = subscriptionInfo;
				if (!logPayload.price || Number(logPayload.price) === 0) {
					if (Number(subscriptionInfo?.initial_price) > 0) {
						logPayload.price = subscriptionInfo?.initial_price;
					} else if (Number(subscriptionInfo?.renewal_price) > 0) {
						logPayload.price = subscriptionInfo?.renewal_price;
					} else {
						logPayload.price = '0.00';
					}
				}
				logPayload.currency = subscriptionInfo?.currency_code ?? logPayload.currency;
				const renewalInfo = {
					renewalPeriod: subscriptionInfo.renewal_period ?? null,
					renewalTerm: subscriptionInfo.renewal_term ?? null,
				};
				logPayload.ltv = await LTVService.getLtv(renewalInfo, logPayload);
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: getSubscriptionInfo',
					},
				});
			}

			if (logType && logPayload.price) {
				await logEvent(logType, logPayload, newUser);
			}

			if (options.redirect) return await goToNextRegPathStepHelper();

			return { data: newUser, error: null };
		} catch (error) {
			const er = !(error instanceof Error) ? new UnknownError(error) : error;
			UserStore.publishFailure(er);
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Upgrade Action',
					paymentType: options.paymentType,
					subscriptionHash,
				},
			});
			return { data: get(UserStore.user), error: er };
		}
	});
}

/**
 * @param {{paymentType: PAYMENT_TYPE; }} options - options to upgrade to.
 * @returns {Promise<{ eligibleProducts: object ; receiptData: object; } | undefined>}
 * @throws Error
 */
async function findEligibleProductsToRestore({ paymentType }) {
	return loadingService.withLoading(async () => {
		try {
			const appService = new AppService();
			const processor = SubscriptionProcessor(ApiService, appService, {
				subscriptionType: SUBSCRIPTION_TYPES.NEW_SUBSCRIPTION,
				paymentType: paymentType,
			});
			const productsToRestore = await processor.findEligibleProductsToRestore();

			if (!productsToRestore) throw new Error('No Existing Purchases (Subscriptions) to Restore');

			return productsToRestore;
		} catch (error) {
			if (!(error instanceof Error)) {
				UserStore.publishFailure(new UnknownError(error));
			} else UserStore.publishFailure(error);

			Sentry.captureException(error, {
				fingerprint: ['findEligibleProductsToRestore', 'ignore'],
				extra: {
					action: 'Error: findEligibleProductsToRestore Action',
					paymentType,
				},
			});
		}
	});
}

/**
 * @param {{ cross_product_aofl_product_uuid: string;
 * 			 subscription_hash: string;
 * 			 origin: string;
 * 			 password: string;}} crossProductDetails - cross product payload
 * @param {object} options - options for handling the request
 * @param {boolean} options.redirect - whether to re-direct after request is complete
 * @param {string} options.paymentType -
 * @returns {Promise<{data: object; error: null | Error }>} - the user state
 */
async function crossProductSignUp(
	crossProductDetails,
	options = { redirect: false, paymentType: PAYMENT_TYPE.CREDIT_CARD }
) {
	return loadingService.withLoading(async () => {
		try {
			const appService = new AppService();
			const processor = SubscriptionProcessor(ApiService, appService, {
				subscriptionType: SUBSCRIPTION_TYPES.EXISTING_SUBSCRIPTION,
				paymentType: options.paymentType,
			});
			const upgradeState = await processor.handleCrossProduct(crossProductDetails);
			const newUserState = await ApiService.validateLogin();
			UserStore.publishSuccess({ ...upgradeState, ...newUserState });

			// Persist analytics identifiers after successful cross-product purchase
			try {
				const identifiers = getAnalyticsIdentifiers();
				const userToProductUuid =
					upgradeState?.user_to_product_uuid ?? newUserState?.user_to_product_uuid;
				if (userToProductUuid) {
					await ApiService.subscriberSetAnalyticsIdentifiers({
						user_to_product_uuid: userToProductUuid,
						identifiers,
						mode: 'merge',
						precedence: 'new_precedence',
					});
				}
			} catch (err) {
				Sentry.captureException(err, {
					extra: {
						action: 'Error: SetAnalyticsIdentifiers after crossProductSignUp',
					},
				});
			}

			if (options.redirect) return await goToNextRegPathStepHelper();
			return { data: newUserState, error: null };
		} catch (error) {
			let er = error instanceof Error ? error : new UnknownError(error);
			UserStore.publishFailure(er);
			// TODO: Monitor error in Sentry, see if this is a good place to log error.
			Sentry.captureException(er, {
				extra: {
					action: 'Error: crossProductSignUp Action',
					paymentType: options.paymentType,
				},
			});
			return { data: get(UserStore.user), error: er };
		}
	});
}

/**
 * Cancels a user's subscription.
 * @param {{
 * 			 cancellation_source: string;
 * 			 subscription_uuid?: string;
 * 			 cancellation_reason?: string; }} cancellationDetails - details of the cancellation request
 * @returns {Promise<{data: {expires_timestamp: number} | null; error: null | Error }>} - the cancellation response with expires_timestamp or user state on redirect
 */
export async function cancelSubscription(cancellationDetails) {
	return loadingService.withLoading(async () => {
		const { cancellation_source, subscription_uuid, cancellation_reason } = cancellationDetails;
		try {
			const cancelResponse = await ApiService.subscriptionCancel({
				subscription_uuid,
				cancellation_source,
				cancellation_reason,
			});

			return { data: cancelResponse, error: null };
		} catch (error) {
			const er = !(error instanceof Error) ? new UnknownError(error) : error;
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Cancel Subscription Action',
					cancellation_source,
					subscription_uuid,
				},
			});
			return { data: null, error: er };
		}
	});
}

/**
 * Resumes a subscription from pause.
 * @returns {Promise<{data: boolean | null; error: null | Error | APIError }>} - the resume response with true or false
 * @throws {UnknownError | APIError}
 */
async function resumeSubscriptionFromPause() {
	return loadingService.withLoading(async () => {
		let resumeSubscriptionFromPause = false;
		let er = null;

		try {
			resumeSubscriptionFromPause = await ApiService.subscriptionResumeSubcriptionFromPause();
		} catch (error) {
			er = !(error instanceof Error) ? new UnknownError(error) : error;
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Resume Subscription From Pause Action',
					resumeSubscriptionFromPause,
				},
			});
		}

		return { data: resumeSubscriptionFromPause, error: er };
	});
}

/**
 * Pauses a user's subscription.
 * @param {{
 * 			 term: string;
 * 			 period: string; }} pauseDetails - details of the pause request
 * @returns {Promise<{data: boolean | null; error: null | Error }>} - the pause response with true or false
 */
async function pauseSubscription(pauseDetails) {
	return loadingService.withLoading(async () => {
		const { term, period } = pauseDetails;
		try {
			await ApiService.subscriptionPause({
				term,
				period,
			});

			return { data: true, error: null };
		} catch (error) {
			const er = !(error instanceof Error) ? new UnknownError(error) : error;
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Pause Subscription Action',
					period,
					term,
				},
			});
			return { data: false, error: er };
		}
	});
}

/**
 * Checks if a user is eligible for pause subscription.
 * @returns {Promise<{data: boolean | null; error: null | Error }>} - the pause response with true or false
 */
async function isSubscriptionPauseEligible() {
	return loadingService.withLoading(async () => {
		let isSubscriptionPauseEligible = { is_eligible: false };
		try {
			isSubscriptionPauseEligible = await ApiService.isSubscriptionPauseEligible();

			return { data: isSubscriptionPauseEligible, error: null };
		} catch (error) {
			const er = !(error instanceof Error) ? new UnknownError(error) : error;
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Is Subscription Pause Eligible Action',
					isSubscriptionPauseEligible,
				},
			});
			return { data: { is_eligible: false }, error: er };
		}
	});
}

/**
 * Helper function to navigate to the next registration path step.
 * @returns {Promise<{data: object; error: Error | null;}>} A promise that resolves when the navigation is complete.
 * @throws {Error}
 */
export async function goToNextRegPathStepHelper() {
	const path = postAuthenticationPathService.getRedirectPath(get(UserStore.user));
	await goto(path);
	return { data: get(UserStore.user), error: null };
}

/**
 * @param {EventType} eventType - type of event
 * @param {{ conversionEventId?: string;
 * 			 currency?: string;
 * 			 ltv?: string
 * 			 product: string;
 * 			 subscriptionInfo: object;
 * 			 subscriptionType: string;
 * 			 price: string; }} payload - payload info.
 * @param {import('$lib/stores/user/store').UserStoreState} userInfo User Information
 * @returns {Promise<object>}
 */
async function logEvent(eventType, payload, userInfo) {
	const { conversionEventId, currency, ltv, price, product, subscriptionType } = payload;
	const appState = get(app);
	return await Logger.log({
		aoflProductId:
			payload?.subscriptionInfo?.aofl_product_uuid ?? ProductExperienceService.AOFL_PRODUCT_UUID,
		detail: {
			adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			conversionEventId: conversionEventId,
			ltv: ltv,
			productCurrency:
				currency || payload?.subscriptionInfo?.currency_code || CURRENCY_CODE.US_DOLLAR,
			productHash: product,
			productPurchasePrice:
				payload?.subscriptionInfo?.initial_price ??
				payload?.subscriptionInfo?.renewal_price ??
				'0.00',
			productPrice: price,
			subscriptionType: subscriptionType,
			udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
			userToProductId: userInfo.userToProductUuid,
		},
		// TODO: remove the getDeviceId() fallback after the 'null' deviceID discrepancy is resolved.
		deviceId: getDeviceId() ?? 'unresolved_device_id_log_event',
		legacyTrackCookie: Cookies.get(COOKIES.TRACK_COOKIE) ?? '',
		meta: {
			...appState.meta,
			linkFromAppPlatform: appState.global?.linkFromAppPlatform,
		},
		type: eventType,
	});
}

/**
 * Saves the subscription state to the store.
 * @param {import('./store').SubscriptionDetails} subscriptionDetail - The details of the subscription to save.
 */
export function saveSubscriptionState(subscriptionDetail) {
	saveSubscriptionStateToStore(subscriptionDetail);
}

/**
 * Gets the user's latest payment information
 * @returns {Promise<{
 *   data: object | null,
 *   error: Error | null
 * }>} Payment info or error
 */
export async function getSubscriptionReceipts() {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.getSubscriptionReceipts();

			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Get Subscription Receipts Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}

/**
 * Gets the user's latest (most recent) subscription receipt with a valid invoice item type
 * @returns {Promise<{
 *   data: object | null,
 *   error: Error | null
 * }>} Latest receipt with valid invoice_item_type or null if none found
 */
async function getLatestSubscriptionReceipt() {
	try {
		const receiptsResult = await getSubscriptionReceipts();

		const receiptsData = receiptsResult.data;
		if (
			receiptsData &&
			'receipts' in receiptsData &&
			Array.isArray(receiptsData.receipts) &&
			receiptsData.receipts.length > 0
		) {
			// Sort receipts by created_timestamp (most recent first)
			const sortedReceipts = receiptsData.receipts.sort((a, b) => {
				const aTime = a?.created_timestamp || 0;
				const bTime = b?.created_timestamp || 0;
				return bTime - aTime;
			});

			return { data: sortedReceipts[0] || null, error: null };
		}

		// No receipts found
		return { data: null, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Get Latest Subscription Receipt Action',
			},
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { error: er, data: null };
	}
}

/**
 * Gets the user's subscription information
 * @param {object} [payload] - Optional payload object.
 * @param {string} [payload.subscription_uuid] - Optional subscription UUID to target a specific subscription.
 * @param {boolean} [payload.include_last_receipt_info] - Optional flag to include last receipt info.
 * @returns {Promise<{
 *   data: {
 *     subscription_category?: string,
 *     subscription_status?: string,
 *     subscription_type?: string,
 *     renewal_term?: string,
 *     renewal_period?: number,
 *     renewal_price?: number,
 *     initial_term?: string,
 *     initial_period?: number,
 *     initial_price?: number,
 *     [key: string]: any
 *   } | null,
 *   error: Error | null
 * }>} Subscription info or error
 */
export async function getSubscriptionInfo(payload = {}) {
	return loadingService.withLoading(async () => {
		try {
			const subscriptionInfo = await ApiService.getSubscriptionInfo(payload);

			return { data: subscriptionInfo, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Get Subscription Info Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}

/**
 * Gets the user's subscriptions information
 * @returns {Promise<{
 *   data: {
 *     subscriptions: Array<{
 *       aofl_product_uuid: string,
 *       is_upgrade_eligible: boolean,
 *       next_billing_timestamp: number,
 *       payment_provider: string,
 *       payment_uuid: string,
 *       role: string,
 *       roles: Array<string>,
 *       subscription_category: string,
 *       subscription_end_timestamp: number,
 *       subscription_plan_label: string,
 *       subscription_start_timestamp: number,
 *       subscription_status: string,
 *       subscription_type: string,
 *       subscription_uuid: string,
 *       third_party_id: string | null,
 *       user_to_product_uuid: string,
 *       [key: string]: any
 *     }>,
 *     total_count: number,
 *     active_count: number,
 *     cancelled_count: number
 *   } | null,
 *   error: Error | null
 * }>} Subscriptions data or error
 */
export async function getSubscriptions() {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.getSubscriptions();

			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Get Subscriptions Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}

/**
 * Checks if the user has a max tier subscription.
 * @returns {Promise<{
 *   data: boolean | null,
 *   error: Error | null
 * }>} Returns true if user has max tier subscription, false otherwise, or error
 */
export async function checkMaxTierSubscription() {
	try {
		const subscriptionsResult = await getSubscriptions();

		if (subscriptionsResult.error) {
			return { data: false, error: subscriptionsResult.error };
		}

		const subscriptions = subscriptionsResult.data;
		if (!subscriptions?.subscriptions) {
			return { data: false, error: null };
		}

		const primarySubscription = subscriptions.subscriptions.find(
			(/** @type {any} */ product) => product.subscription_category === 'primary'
		);

		if (!primarySubscription) {
			return { data: false, error: null };
		}

		const roles = primarySubscription.roles || [];
		const isMaxTier = roles.includes(AOFL_2_0_ROLES.abcmousev2_user_max_tier);

		return { data: isMaxTier, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Check Max Tier Subscription Action',
			},
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { data: false, error: er };
	}
}

/**
 * Updates the user's payment information
 * @param {object} payment - The payment information to update.
 * @param {string} subscription_uuid - The subscription UUID to update.
 * @param {string} payment_provider - The payment provider to update.
 * @returns {Promise<{data: object | null, error: Error | null}>} The updated payment information or error
 */

export const subscriptionUpdatePaymentInfo = async (payment, payment_provider) => {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.subscriptionUpdatePaymentInfo({
				payment_provider,
				payment_provider_info: {
					card_name: payment.cardName,
					card_number: payment.cardNumber,
					card_expiration_date: payment.expDate,
					card_cvv: payment.cardCvv,
					card_zip: payment.zipCode,
				},
			});
			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: subscriptionUpdatePaymentInfo Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
};

/**
 * Gets the user's receipts by subscription UUID
 * @param {string} subscriptionUuid - The subscription UUID.
 * @returns {Promise<{
 *   data: object | null,
 *   error: Error | null
 * }>} Payment info or error
 */
export async function getReceiptsBySubscriptionUuid(subscriptionUuid) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.getReceiptsBySubscriptionUuid(subscriptionUuid);

			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Get Receipts By Subscription UUID Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}

/**
 * Gets the user's latest (most recent) receipt with a valid invoice item type by subscription UUID
 * @param {string} subscriptionUuid - The subscription UUID.
 * @returns {Promise<{
 *   data: object | null,
 *   error: Error | null
 * }>} Latest receipt with valid invoice_item_type or null if none found
 */
export async function getLatestReceiptBySubscriptionUuid(subscriptionUuid) {
	try {
		const receiptsResult = await getReceiptsBySubscriptionUuid(subscriptionUuid);

		const receiptsData = receiptsResult.data;
		if (
			receiptsData &&
			'receipts' in receiptsData &&
			Array.isArray(receiptsData.receipts) &&
			receiptsData.receipts.length > 0
		) {
			// Sort receipts by created_timestamp (most recent first)
			const sortedReceipts = receiptsData.receipts.sort((a, b) => {
				const aTime = a?.created_timestamp || 0;
				const bTime = b?.created_timestamp || 0;
				return bTime - aTime;
			});

			return { data: sortedReceipts[0] || null, error: null };
		}

		// No receipts found
		return { data: null, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Get Latest Receipt By Subscription UUID Action',
			},
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { error: er, data: null };
	}
}

/**
 * Checks if the user is eligible for one click cancellation.
 * @param {object} payload - The payload.
 * @param {string} payload.subscriptionUuid - Optional subscription uuid.
 * @returns {Promise<{
 *   data: { is_eligible: boolean, reason: string, country: string, is_offer_eligible: boolean } | null,
 *   error: Error | null
 * }>} Eligibility info or error
 */
export async function isOneClickCancellationEligible(payload) {
	return loadingService.withLoading(async () => {
		try {
			const response =
				/** @type {{ is_eligible: boolean, reason: string, country: string, is_offer_eligible: boolean }} */ (
					await ApiService.isOneClickCancellationEligible(payload || {})
				);

			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Is One Click Cancellation Eligible Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}

/**
 * Adds credential to existing account.
 * @param {string} subscriptionPlanHash - Subscription plan hash
 * @param {string} actionSource - Action source
 * @returns {Promise<object>} Upgrade request result or error
 */
export async function upgradeRequest(subscriptionPlanHash, actionSource) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.upgradeRequest(subscriptionPlanHash, actionSource);
			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: upgradeRequest Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { data: get(UserStore.user), error: er };
		}
	});
}
/**
 * Retrieves printables info.
 * @param {number} page = 1
 * @param {number} itemsPerPage = 10
 * @returns {Promise<{
 *   data: object | null,
 *   error: Error | null
 * }>} A promise that resolves with the result of the update request.
 */
export async function getPrintablesActivity(page = 1, itemsPerPage = 10) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.getPrintablesInfo(page, itemsPerPage);
			if (!response) {
				return { data: null, error: new UnknownError('No response from getPrintablesInfo') };
			}
			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Get Printables Activity Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}

/**
 * Retrieves printables info.
 * @param {number} page = 1
 * @param {number} itemsPerPage = 10
 * @returns {Promise<{
 *   data: object | null,
 *   error: Error | null
 * }>} A promise that resolves with the result of the update request.
 */
export async function getPrintablesFeatureActivities(page = 1, itemsPerPage = 10) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.getPrintablesFeatureActivities(page, itemsPerPage);
			if (!response) {
				return {
					data: null,
					error: new UnknownError('No response from getPrintablesFeatureActivities'),
				};
			}
			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Get Printables Activity Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}

/**
 * Retrieves printables filters.
 * @returns {Promise<{
 *   data: object | null,
 *   error: Error | null
 * }>} A promise that resolves with the result of the update request.
 */
export async function getPrintablesFilters() {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.getPrintablesFilters();
			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Get Printables Filters Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}
